import { InfoCircleOutlined } from '@ant-design/icons'
import {
  OrderBusinessCardType,
  useBusinessCardOrderDetailMutation,
  useCustomerAccountListMutation,
  usePossibleBusinessCardMutation,
} from '@app/store/reducers/salaryCards/businessCardOrder.api'
import { CardCurrenyType, CardType } from '@app/store/types/salaryCard'
import Dialog from '@shared/Dialog'
import { InputField } from '@shared/InputField'
import { ModalProvider } from '@shared/Modal'
import RadioField from '@shared/RadioField/RadioField'
import SelectGlobal from '@shared/Select'
import { Col, Form, Image, Row, Spin, Tooltip, Typography } from 'antd'
import React, { useEffect, useMemo, useRef } from 'react'
import { Controller, set, useFormContext, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { usePossibleCard } from '../../../models/usePossibleCard'
import styles from './OrderBlockOne.module.scss'
import { useRouteMatch } from 'react-router-dom'

type LinkDatum = { keyword: string; url: string; displayText: string }

export const convertMultipleLinks = (
  message: string,
  linkData: LinkDatum[]
) => {
  if (!message) return null
  let result = message

  linkData.forEach(({ keyword, url, displayText }) => {
    const regex = new RegExp(`\\b${keyword}\\b`, 'gi')
    result = result.replace(
      regex,
      `<LINK_${encodeURIComponent(keyword)}_${encodeURIComponent(
        url
      )}_${encodeURIComponent(displayText || keyword)}_LINK>`
    )
  })

  const parts = result.split(/(<LINK_.*?_LINK>)/)
  return parts.map((part, index) => {
    const linkMatch = part.match(/^<LINK_(.*?)_(.*?)_(.*?)_LINK>$/)
    if (linkMatch) {
      const [, encKeyword, encUrl, encText] = linkMatch
      const url = decodeURIComponent(encUrl)
      const displayText = decodeURIComponent(encText)

      return (
        <a
          key={`${encKeyword}_${index}`}
          href={url}
          style={{ color: '#90c5ff', textDecoration: 'underline' }}
          target="_blank"
          rel="noopener noreferrer"
        >
          {displayText}
        </a>
      )
    }
    return <React.Fragment key={index}>{part}</React.Fragment>
  })
}

function OrderBlockOne() {
  const { t } = useTranslation()
  const location = useRouteMatch()
  
  const {
    availablePaymentSystems,
    cardList,
    cardDuration,
    selectedCard,
    cardImage,
    setPaymentSystem,
    setSelectedCard,
    setPossibleCard,
    setCardDuration,
  } = usePossibleCard()

  const { control, setValue, resetField, trigger } =
    useFormContext<OrderBusinessCardType>()

  useEffect(() => {
    setValue('cardName', selectedCard?.cardName, { shouldValidate: true })
    setValue('cardImage', cardImage?.small)
  }, [selectedCard])

  const [
    mutate,
    { data, isSuccess: isCALSuccess, isLoading: isLoadingAccount },
  ] = useCustomerAccountListMutation()

  const [fetchPossibleBusinessCard, { isSuccess, isLoading, reset }] =
    usePossibleBusinessCardMutation()

  const [_, { data: orderData, isSuccess: isSuccessOD }] =
    useBusinessCardOrderDetailMutation({
      fixedCacheKey: 'businessCardOrderDetail',
    })

  const watchCcy = useWatch({ control, name: 'cardCurrency' })
  const watchcardId = useWatch({ control, name: 'cardId' })

  const fetchAccountList = async (ccy: string) => {
    try {
      const data = await mutate({
        currency: ccy,
      }).unwrap()

      Dialog.info({
        title: t('common.attention'),
        modalType: data?.status === 'WARNING' ? 'info' : 'failed',
        width: 500,
        color: data?.status === 'WARNING' ? '#FF9500' : '#FF3333',
        message: convertMultipleLinks(data?.message, [
          {
            displayText: t('businessCardOrders.labels.open_new_account'),
            keyword: 'opennewaccount',
            url: process.env.REACT_RIS_LINK || 'https://dvp.bankrespublika.az/auth/login?path=/auth/login',
          },
        ]),
        okText: t('orders.salaryCardOrder.buttonsLabels.close'),
      })

      return data
    } catch (error) {}
  }

  useEffect(() => {
      ;(async () => {
        if(watchCcy) {
          const data = await fetchAccountList(watchCcy)

        if (
          isSuccessOD &&
          location.path === '/orders/business-card/update/:id' &&
          data?.accounts?.find(v => v.accountNo == orderData?.cardAccount)
        ) {
          setValue('cardAccount', orderData?.cardAccount, {
            shouldValidate: true,
          })

          await fetchPossibleBusinessCard({ iban: orderData?.cardAccount })
            .unwrap()
            .then((res) => {
              setPossibleCard(res)
              setSelectedCard(String(orderData?.cardId))
              resetField('cardSystem', { defaultValue: orderData?.cardSystem })
              resetField('cardDuration', {
                defaultValue: orderData?.cardDuration,
              })
              resetField('cardName', { defaultValue: orderData?.cardName })
              resetField('cardId', { defaultValue: String(orderData?.cardId) })
              resetField('commission.issueFee', {
                defaultValue: orderData?.commission?.issueFee || 0,
              })
              trigger('cardDuration')
            })
            .catch((err) => {
              Dialog.info({
                title: t('common.attention'),
                modalType: 'failed',
                message: err?.data,
                okText: t('orders.salaryCardOrder.buttonsLabels.close'),
              })
            })
        }
        }
      })()
  }, [watchCcy])

  const resetRef = useRef(reset)
  resetRef.current = reset
  const cardCurrencyOptions = useMemo(() => {
    return Object.entries(CardCurrenyType).map(([value, title]) => ({
      value,
      title,
      onClick: () => {
        // fetchAccountList(value)
        resetRef.current()
        setValue('cardAccount', null, { shouldValidate: true })
        resetField('cardId', { defaultValue: null })
        resetField('cardDuration', { defaultValue: null })
        resetField('cardName', { defaultValue: null })
        resetField('cardSystem', { defaultValue: null })
        resetField('commission.issueFee', { defaultValue: 0 })
      },
    }))
  }, [])

  useEffect(() => {
    setValue('isMain', availablePaymentSystems.main);
    
    if (availablePaymentSystems.visa) {
      setValue('cardSystem', CardType.VISA, {
        shouldValidate: true,
      })
    } else if (availablePaymentSystems.mastercard) {
      setValue('cardSystem', CardType.MASTERCARD, {
        shouldValidate: true,
      })
    }
  }, [availablePaymentSystems])

  const watchedCardSystem = useWatch({ control, name: 'cardSystem' })

  useEffect(() => {
    if (watchedCardSystem) {
      setPaymentSystem(watchedCardSystem)
    }
  }, [watchedCardSystem])

  useEffect(() => {
    if (!watchcardId) {
      setCardDuration([])
    }
  }, [watchcardId])

  return (
    <Spin
      spinning={isLoading || isLoadingAccount}
      wrapperClassName={styles.wrapperClassName}
      style={{ height: '100%' }}
    >
      <Form
        labelCol={{ span: 7 }}
        wrapperCol={{ span: 24 }}
        layout="horizontal"
        style={{ height: '100%' }}
      >
        <Row
          gutter={[0, 20]}
          style={{
            height: '100%',
            boxSizing: 'border-box',
            backgroundColor: '#F3F6FA',
            borderRadius: 16,
            padding: 20,
            flexDirection: 'column',
          }}
        >
          <Col>
            <Controller
              name="cardCurrency"
              control={control}
              defaultValue={CardCurrenyType.AZN}
              render={({
                field: { name, value, onChange },
                fieldState: { error, invalid },
              }) => (
                <RadioField
                  name={name}
                  value={value}
                  onChange={onChange}
                  style={{ marginBottom: 0 }}
                  validateStatus={invalid ? 'error' : 'success'}
                  help={invalid ? error?.message : ''}
                  label={t('orders.salaryCardOrder.inputLabels.cardCurrency')}
                  radioOptions={cardCurrencyOptions}
                />
              )}
            />
          </Col>
          <Col>
            <Controller
              name="cardAccount"
              control={control}
              render={({
                field: { name, onChange, value },
                fieldState: { invalid, error },
              }) => {
                return (
                  <SelectGlobal
                    onSelect={(v) => {
                      resetField('cardImage', { defaultValue: undefined })
                      resetField('isMain', { defaultValue: false })
                      resetField('cardId', { defaultValue: null })
                      resetField('cardDuration', { defaultValue: null })
                      resetField('cardName', { defaultValue: null })
                      resetField('cardSystem', { defaultValue: null })
                      resetField('commission.issueFee', { defaultValue: 0 })

                      fetchPossibleBusinessCard({
                        iban: v,
                      })
                        .unwrap()
                        .then((res) => {
                          setPossibleCard(res)
                          trigger([
                            'cardId',
                            'cardName',
                            'cardDuration',
                            'cardSystem',
                          ])
                        })
                        .catch((err) => {
                          Dialog.info({
                            title: t('common.attention'),
                            modalType: 'failed',
                            message: err?.data,
                            okText: t(
                              'orders.salaryCardOrder.buttonsLabels.close'
                            ),
                          })
                        })
                    }}
                    style={{ margin: 0 }}
                    label={
                      <>
                        <Typography.Text>
                          {t('businessCardOrders.labels.cardAccount')}
                        </Typography.Text>
                        <Tooltip title={t('businessCardOrders.accountInfo')}>
                          <InfoCircleOutlined
                            style={{
                              fontSize: 14,
                              marginLeft: '4px',
                              cursor: 'pointer',
                              color: '#015CD1',
                            }}
                          />
                        </Tooltip>
                      </>
                    }
                    name={name}
                    value={value}
                    onChange={onChange}
                    allowClear={false}
                    placeholder={t('businessCardOrders.labels.select1')}
                    options={
                      isCALSuccess
                        ? data?.accounts?.map((v) => ({
                            label: v.accountNo,
                            value: v.accountNo,
                          }))
                        : []
                    }
                    validateStatus={invalid ? 'error' : 'success'}
                    help={invalid ? t(error?.message) : ''}
                  />
                )
              }}
            />
          </Col>
          <Col>
            <Controller
              name="cardSystem"
              disabled={!isSuccess}
              control={control}
              render={({
                field: { name, value, onChange, disabled },
                fieldState: { invalid, error },
              }) => {
                return (
                  <RadioField
                    name={name}
                    value={value}
                    onChange={onChange}
                    disabled={disabled}
                    style={{ margin: 0 }}
                    validateStatus={invalid ? 'error' : 'success'}
                    help={invalid ? t(error?.message) : ''}
                    label={
                      <>
                        {t('businessCardOrders.labels.paySytsem')}
                        <Tooltip
                          placement="right"
                          overlayInnerStyle={{ width: 500 }}
                          title={convertMultipleLinks(
                            t('businessCardOrders.cardSystemInfo'),
                            [
                              {
                                displayText: 'Visa Platinum Business',
                                keyword: 'kecidlink',
                                url: 'https://www.visa.com.az/run-your-business/visa-platinum-business.html',
                              },
                            ]
                          )}
                        >
                          <InfoCircleOutlined
                            style={{
                              fontSize: 14,
                              marginLeft: '4px',
                              cursor: 'pointer',
                              color: '#015CD1',
                            }}
                          />
                        </Tooltip>
                      </>
                    }
                    radioOptions={Object.entries(CardType)
                      .reverse()
                      .map(([value, title]) => ({
                        value,
                        disabled:
                          !availablePaymentSystems[
                            value.toLowerCase() as keyof typeof availablePaymentSystems
                          ] || !isSuccess,
                        title: t(`orders.salaryCardOrder.constants.${title}`),
                        onClick(e) {
                          setValue('cardId', undefined, {
                            shouldValidate: true,
                          })
                          setValue('cardDuration', undefined, {
                            shouldValidate: true,
                          })
                          setValue('commission.issueFee', 0, {
                            shouldValidate: true,
                          })
                          setSelectedCard(null)
                        },
                      }))}
                  />
                )
              }}
            />
          </Col>

          <Col>
            <Form.Item
              style={{ margin: 0 }}
              labelCol={{ span: 7, className: styles.labelCls }}
              label={
                <>
                  <Typography.Text>
                    {t('businessCardOrders.labels.cardName')}
                  </Typography.Text>
                  <Tooltip
                    overlayInnerStyle={{ width: 500 }}
                    title={convertMultipleLinks(
                      t('businessCardOrders.cardInfo'),
                      [
                        {
                          displayText:
                            'https://www.bankrespublika.az/az/non-credit-products/business-cards ',
                          keyword: 'cardInfoLink',
                          url: 'https://www.bankrespublika.az/az/non-credit-products/business-cards ',
                        },
                      ]
                    )}
                  >
                    <InfoCircleOutlined
                      style={{
                        fontSize: 14,
                        marginLeft: '4px',
                        cursor: 'pointer',
                        color: '#015CD1',
                      }}
                    />
                  </Tooltip>
                </>
              }
            >
              <Row gutter={[12, 0]} justify="space-between">
                <Col span={19}>
                  <Controller
                    name="cardId"
                    disabled={!isSuccess}
                    control={control}
                    render={({
                      field: { name, onChange, value },
                      fieldState: { invalid, error },
                    }) => {
                      return (
                        <SelectGlobal
                          style={{ margin: 0 }}
                          label=""
                          disabled={!isSuccess}
                          onSelect={(v) => {
                            resetField('cardDuration', { defaultValue: null })
                            resetField('commission.issueFee', {
                              defaultValue: 0,
                            })
                            trigger('cardDuration')
                            setSelectedCard(v)
                          }}
                          name={name}
                          onChange={onChange}
                          value={value}
                          allowClear={false}
                          placeholder={t('businessCardOrders.labels.select')}
                          options={cardList}
                          validateStatus={
                            isSuccess && invalid ? 'error' : 'success'
                          }
                          help={isSuccess && invalid ? t(error?.message) : ''}
                        />
                      )
                    }}
                  />
                </Col>
                <Col>
                  <Form.Item>
                    <ModalProvider
                      title={selectedCard?.cardName}
                      centered
                      width={500}
                      modalContent={
                        <Image preview={false} src={cardImage?.original} />
                      }
                    >
                      {({ openModal }) => (
                        <div
                          style={{
                            borderRadius: '4px',
                            padding: '4px',
                            backgroundColor: '#fff',
                            border: '1px solid #D9E3EC',
                            cursor: 'pointer',
                            width: cardImage?.small ? 'auto' : 57,
                            height: cardImage?.small ? 'auto' : 39,
                          }}
                          onClick={() => cardImage && openModal('')}
                        >
                          {cardImage?.small && (
                            <Image
                              width={49}
                              height={30}
                              preview={false}
                              src={cardImage?.small}
                            />
                          )}
                        </div>
                      )}
                    </ModalProvider>
                  </Form.Item>
                </Col>
              </Row>
            </Form.Item>
          </Col>

          <Col>
            <Row gutter={12}>
              <Col span={12}>
                <Controller
                  name="cardDuration"
                  control={control}
                  render={({
                    field: { name, value, onChange },
                    fieldState: { invalid, error },
                  }) => {
                    return (
                      <SelectGlobal
                        style={{ margin: 0 }}
                        disabled={!isSuccess}
                        label={t('businessCardOrders.labels.cardDuration')}
                        name={name}
                        value={value}
                        onChange={onChange}
                        onSelect={(v) => {
                          setValue(
                            'commission.issueFee',
                            cardDuration.find((vv) => vv.value == v)?.fee ?? 0
                          )
                          setValue('cardTariffId', cardDuration.find((vv) => vv.value == v)?.cardTariffId ?? 0)
                          }
                        }
                        allowClear={false}
                        placeholder={t('businessCardOrders.labels.select')}
                        options={cardDuration}
                        labelCol={{ span: 14 }}
                        wrapperCol={{ span: 24 }}
                        validateStatus={
                          isSuccess && invalid ? 'error' : 'success'
                        }
                        help={isSuccess && invalid ? t(error?.message) : ''}
                      />
                    )
                  }}
                />
              </Col>
              <Col span={12}>
                <Controller
                  name="commission.issueFee"
                  control={control}
                  render={({
                    field: { name, onChange, value },
                    fieldState: { error, invalid },
                  }) => (
                    <InputField
                      name={name}
                      onChange={onChange}
                      value={Number(value).toFixed(2)}
                      disabled
                      label={`${t('businessCardOrders.labels.issueFree')}, AZN`}
                      wrapperCol={{ span: 8 }}
                      labelCol={{ span: 16 }}
                      style={{ margin: 0 }}
                      validateStatus={invalid ? 'error' : 'success'}
                      help={invalid ? t(error?.message) : ''}
                    />
                  )}
                />
              </Col>
            </Row>
          </Col>
        </Row>
      </Form>
    </Spin>
  )
}

export default OrderBlockOne
